// @ts-nocheck

import oneTwoThree from "@iconify/icons-bi/123";
import bank from "@iconify/icons-bi/bank";
import chartBar from "@iconify/icons-bi/bar-chart";
import calendar from "@iconify/icons-bi/calendar";
import clipboard2Check from "@iconify/icons-bi/clipboard2-check";
import clockHistory from "@iconify/icons-bi/clock-history";
import dollarSign from "@iconify/icons-bi/currency-dollar";
import diagram2 from "@iconify/icons-bi/diagram-2";
import fonts from "@iconify/icons-bi/fonts";
import graphUpArrow from "@iconify/icons-bi/graph-up-arrow";
import justifyLeft from "@iconify/icons-bi/justify-left";
import checklist from "@iconify/icons-bi/list-task";
import patchQuestion from "@iconify/icons-bi/patch-question";
import percent from "@iconify/icons-bi/percent";
import textareaT from "@iconify/icons-bi/textarea-t";
import checksGrid from "@iconify/icons-bi/ui-checks-grid";
import vectorPen from "@iconify/icons-bi/vector-pen";
import radioButtonChecked from "@iconify/icons-mdi/radiobox-marked";
import { type IconifyIcon } from "@iconify/react/dist/offline";

import { ICONS } from "src/utils/icons";
import {
  AggregationTypeEnum,
  BlockTypeEnum,
  CollectionPlanFrequencyEnum,
  DateStrategyEnum,
  DecimalPlacesTypeEnum,
  DeliverableCollectionStatusEnum,
  DeliverableCollectionStrategyEnum,
  FrequencyTypeEnum,
  InterpolationVariableEnum,
  MonthEnum,
  NotificationDeliveryCadenceEnum,
  PeriodTypeEnum,
  PropertyTypeEnum,
  ReferenceFieldEnum,
  ReferenceTypeEnum,
  ReturnTypeEnum,
  RouteNames,
  ScaleEnum,
  TaskAssignmentTypeEnum,
  TaskTypeEnum,
} from "src/client";
import { toOptions } from "src/utils/toOptions";

export const SUB_NAV_BAR_HEIGHT = 48;
export const SIDEBAR_WIDTH = 260;

// Matches the backend field/formula description column (varchar(300)).
export const FIELD_DESCRIPTION_MAX_LENGTH = 300;

export const COMPANY_EMAIL_DOMAINS = [
  "lumonic.com",
  "morningstar.com",
  "pitchbook.com",
];

interface FormatDetails {
  icon: IconifyIcon;
  label: string;
  mask?: string;
}

const FORMULA_DETAILS_BY_RETURN_TYPE: Record<ReturnTypeEnum, FormatDetails> = {
  [ReturnTypeEnum.CURRENCY]: {
    icon: dollarSign,
    label: "Currency",
    mask: "currency",
  },
  [ReturnTypeEnum.PERCENT]: {
    icon: percent,
    label: "Percentage",
    mask: "percentage",
  },
  [ReturnTypeEnum.NUMBER]: {
    icon: oneTwoThree,
    label: "Number",
    mask: "decimal",
  },
  [ReturnTypeEnum.SHORT_TEXT]: { icon: fonts, label: "Short text" },
  [ReturnTypeEnum.LONG_TEXT]: { icon: justifyLeft, label: "Long text" },
  [ReturnTypeEnum.DATE]: { icon: calendar, label: "Date" },
  [ReturnTypeEnum.YES_OR_NO]: { icon: patchQuestion, label: "Yes or no" },
};

export const FORMULA_RETURN_TYPE_OPTIONS = Object.keys(
  FORMULA_DETAILS_BY_RETURN_TYPE
).map((key) => ({
  label: FORMULA_DETAILS_BY_RETURN_TYPE[key].label,
  value: key,
}));

export const FIELD_DETAILS_BY_RETURN_TYPE: Record<
  ReturnTypeEnum,
  FormatDetails
> = {
  ...FORMULA_DETAILS_BY_RETURN_TYPE,
  [ReturnTypeEnum.SINGLE_SELECT]: {
    icon: radioButtonChecked,
    label: "Single select",
  },
  [ReturnTypeEnum.MULTI_SELECT]: { icon: checksGrid, label: "Multi select" },
};

export const FIELD_DETAILS_BY_AGG_METHOD: Record<string, FormatDetails> = {
  [AggregationTypeEnum.SUM]: {
    icon: oneTwoThree,
    label: "Sum",
  },
  [AggregationTypeEnum.AVERAGE]: {
    icon: oneTwoThree,
    label: "Average",
  },
  [AggregationTypeEnum.END_OF_PERIOD]: {
    icon: calendar,
    label: "End of Period",
  },
};

export const DETAILS_BY_RETURN_TYPE: Record<string, FormatDetails> = {
  ...FIELD_DETAILS_BY_RETURN_TYPE,
  [ReturnTypeEnum.DOCUMENT]: { icon: ICONS.document, label: "Document" },
  [ReturnTypeEnum.YES_OR_NO]: { icon: patchQuestion, label: "Yes or no" },
};

export const DETAILS_BY_BLOCK_TYPE = {
  ...DETAILS_BY_RETURN_TYPE,
  [BlockTypeEnum.SIGNATURE]: {
    icon: vectorPen,
    label: "Signature",
  },
  [BlockTypeEnum.RICH_TEXT]: {
    icon: textareaT,
    label: "Description",
  },
  [BlockTypeEnum.CONDITIONAL_GROUP]: {
    icon: diagram2,
    label: "Conditional group",
  },
  [BlockTypeEnum.SECTION]: {
    icon: ICONS.taskTemplate,
    label: "Section",
  },
  [BlockTypeEnum.FINANCIAL_ACCOUNTS]: {
    icon: ICONS.bankAccount,
    label: "Bank accounts",
  },
  [BlockTypeEnum.DATABASE]: {
    icon: ICONS.database,
    label: "Database",
  },
  [BlockTypeEnum.SINGLE_SELECT]: {
    icon: radioButtonChecked,
    label: "Single select",
  },
  [BlockTypeEnum.MULTI_SELECT]: {
    icon: checksGrid,
    label: "Multi-select",
  },
};

export const BLOCK_TYPE_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(DETAILS_BY_BLOCK_TYPE).map(([blockType, details]) => [
      blockType,
      details.label,
    ])
  ),
  CHECKBOX: "Checkbox",
  FILE_UPLOAD: "File upload",
  HORIZONTAL_GROUP: "Horizontal group",
  VERTICAL_GROUP: "Vertical group",
  CALCULATED: "Calculated",
  TABLE: "Table",
};

export const getBlockTypeLabel = (
  blockType?: string | null
): string | undefined => {
  if (!blockType) return undefined;
  return BLOCK_TYPE_LABELS[blockType];
};

interface PropertyDetails {
  icon: IconifyIcon;
  label: string;
}

// Default icon for unknown property types
const DEFAULT_PROPERTY_ICON = ICONS.staticField;
const DEFAULT_PROPERTY_LABEL = "Unknown property";

const getDetailsForProperty = (
  propertyType: PropertyTypeEnum,
  returnType?: ReturnTypeEnum
): PropertyDetails => {
  let icon;
  let label;
  let propertyTypeLabel;
  let propertyTypeIcon;

  if (propertyType === PropertyTypeEnum.FIELD) {
    icon = ICONS.staticField;
    label = "Field";
    if (returnType) {
      const details = DETAILS_BY_RETURN_TYPE[returnType];
      icon = details.icon;
      label = details.label;
    }
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.FORMULA) {
    icon = ICONS.formula;
    label = "Formulas";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.LINE_ITEM) {
    icon = dollarSign;
    label = "Financials";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.DOCUMENT) {
    icon = ICONS.document;
    label = "Document";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (
    propertyType === PropertyTypeEnum.ASSET_ACCOUNT ||
    propertyType === PropertyTypeEnum.ASSET_ACCOUNT_FEATURE
  ) {
    icon = ICONS.bankAccount;
    label = "Bank account";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.DATABASE_COLUMN) {
    icon = ICONS.database;
    label = "Database column";
    if (returnType) {
      const details = DETAILS_BY_RETURN_TYPE[returnType];
      icon = details.icon;
      label = details.label;
    }
    propertyTypeLabel = "Database column";
    propertyTypeIcon = ICONS.database;
  } else if (
    propertyType === PropertyTypeEnum.COVENANT ||
    propertyType === PropertyTypeEnum.COVENANT_FEATURE
  ) {
    icon = clipboard2Check;
    label = "Covenant";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (
    propertyType === PropertyTypeEnum.DELIVERABLE ||
    propertyType === PropertyTypeEnum.DELIVERABLE_FEATURE
  ) {
    icon = checklist;
    label = "Deliverable";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.METRIC) {
    icon = chartBar;
    label = "Metric";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType == PropertyTypeEnum.CASHFLOW_FORMULA) {
    icon = graphUpArrow;
    label = "Performance metrics";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else if (propertyType === PropertyTypeEnum.ROLE) {
    icon = ICONS.role;
    label = "Automation role";
    propertyTypeLabel = label;
    propertyTypeIcon = icon;
  } else {
    // Dont explode - return a default icon instead
    icon = DEFAULT_PROPERTY_ICON;
    label = DEFAULT_PROPERTY_LABEL;
    propertyTypeLabel = DEFAULT_PROPERTY_LABEL;
    propertyTypeIcon = DEFAULT_PROPERTY_ICON;
    console.warn(`Unrecognized propertyType: ${propertyType}`);
  }

  return { icon, label, propertyTypeLabel, propertyTypeIcon };
};

export const getIconForProperty = (
  propertyType?: PropertyTypeEnum | null,
  returnType?: ReturnTypeEnum | null,
  blockType?: BlockTypeEnum | null // todo - this is dumb, it shouldn't live here
): IconifyIcon => {
  if (blockType in DETAILS_BY_BLOCK_TYPE)
    return DETAILS_BY_BLOCK_TYPE[blockType].icon;

  if (!propertyType) return DEFAULT_PROPERTY_ICON;

  const { icon } = getDetailsForProperty(propertyType, returnType);

  return icon;
};

export const getPropertyTypeLabelForProperty = (
  propertyType: PropertyTypeEnum,
  returnType?: ReturnTypeEnum
): string => {
  /*
  Property type label is the same as the property label except it will return "Metric" instead of
  the format type for expression based properties (i.e. "Currency", "Decimal")
   */
  const { propertyTypeLabel } = getDetailsForProperty(propertyType, returnType);

  return propertyTypeLabel;
};

export const RETURN_TYPE_OPTIONS = Object.keys(DETAILS_BY_RETURN_TYPE).map(
  (key) => ({
    label: DETAILS_BY_RETURN_TYPE[key].label,
    value: key,
  })
);

export const getReturnTypeOptions = (excludedKeys: string[]) =>
  Object.keys(DETAILS_BY_RETURN_TYPE)
    .filter((key) => !excludedKeys.includes(key))
    .map((key) => ({
      label: DETAILS_BY_RETURN_TYPE[key].label,
      value: key,
    }));

export const PERIOD_BY_FREQUENCY_TYPE: Partial<
  Record<FrequencyTypeEnum, string>
> = {
  [FrequencyTypeEnum.MONTH]: "month",
  [FrequencyTypeEnum.QUARTER]: "quarter",
  [FrequencyTypeEnum.ANNUAL]: "year",
};

export const LABEL_BY_FREQUENCY_TYPE: Partial<
  Record<FrequencyTypeEnum, string>
> = {
  [FrequencyTypeEnum.MONTH]: "Monthly",
  [FrequencyTypeEnum.QUARTER]: "Quarterly",
  [FrequencyTypeEnum.ANNUAL]: "Annually",
};

const LABEL_BY_SCALE: Partial<Record<ScaleEnum, string>> = {
  [ScaleEnum.STANDARD]: "Standard",
  [ScaleEnum.THOUSANDS]: "Thousands",
  [ScaleEnum.MILLIONS]: "Millions",
};

export const INTEGER_BY_DECIMAL_PLACES_TYPE: Partial<
  Record<ScaleEnum, string>
> = {
  [DecimalPlacesTypeEnum.ZERO]: 0,
  [DecimalPlacesTypeEnum.ONE]: 1,
  [DecimalPlacesTypeEnum.TWO]: 2,
  [DecimalPlacesTypeEnum.THREE]: 3,
  [DecimalPlacesTypeEnum.FOUR]: 4,
  [DecimalPlacesTypeEnum.FIVE]: 5,
  [DecimalPlacesTypeEnum.SIX]: 6,
};

const LABEL_BY_DECIMAL_PLACES_TYPE: Partial<Record<ScaleEnum, string>> = {
  [DecimalPlacesTypeEnum.ZERO]: "0 decimal places",
  [DecimalPlacesTypeEnum.ONE]: "1 decimal place",
  [DecimalPlacesTypeEnum.TWO]: "2 decimal places",
  [DecimalPlacesTypeEnum.THREE]: "3 decimal places",
  [DecimalPlacesTypeEnum.FOUR]: "4 decimal places",
  [DecimalPlacesTypeEnum.FIVE]: "5 decimal places",
  [DecimalPlacesTypeEnum.SIX]: "6 decimal places",
};

export const DECIMAL_PLACES_TYPE_OPTIONS = toOptions(
  LABEL_BY_DECIMAL_PLACES_TYPE,
  false
);

export const LABEL_BY_PERIOD_TYPE: Record<PeriodTypeEnum, string> = {
  [PeriodTypeEnum.D]: "Day",
  [PeriodTypeEnum.M]: "Month",
  [PeriodTypeEnum.Q]: "Calendar Quarter",
  [PeriodTypeEnum.FQ]: "Fiscal Quarter",
  [PeriodTypeEnum.Y]: "Calendar Year",
  [PeriodTypeEnum.FY]: "Fiscal Year",
  [PeriodTypeEnum.LTM]: "LTM",
  [PeriodTypeEnum.NTM]: "NTM",
  [PeriodTypeEnum.QTD]: "QTD",
  [PeriodTypeEnum.FQTD]: "Fiscal QTD",
  [PeriodTypeEnum.YTD]: "YTD",
  [PeriodTypeEnum.FYTD]: "Fiscal YTD",
};

/**
 * WINDOW period types: derived aggregations (a trailing/forward window or a *-to-date), NOT a
 * cadence. A window has no inherent emit grain, so it can be viewed on a chosen `cadence=`.
 * Mirrors PeriodTypeEnum.PERIOD_WINDOW_TYPES on the backend.
 */
export const PERIOD_WINDOW_TYPES: ReadonlySet<PeriodTypeEnum> = new Set([
  PeriodTypeEnum.LTM,
  PeriodTypeEnum.NTM,
  PeriodTypeEnum.YTD,
  PeriodTypeEnum.FYTD,
  PeriodTypeEnum.QTD,
  PeriodTypeEnum.FQTD,
]);

/** CLEAN basis grains: a real reporting cadence (D/M/Q/FQ/Y/FY), not a window. */
export const PERIOD_CLEAN_BASES: ReadonlySet<PeriodTypeEnum> = new Set([
  PeriodTypeEnum.D,
  PeriodTypeEnum.M,
  PeriodTypeEnum.Q,
  PeriodTypeEnum.FQ,
  PeriodTypeEnum.Y,
  PeriodTypeEnum.FY,
]);

export const isWindowPeriodType = (
  period?: PeriodTypeEnum | string | null
): boolean =>
  !!period && PERIOD_WINDOW_TYPES.has(String(period).toUpperCase() as PeriodTypeEnum);

export const isCleanGrain = (
  period?: PeriodTypeEnum | string | null
): boolean =>
  !!period && PERIOD_CLEAN_BASES.has(String(period).toUpperCase() as PeriodTypeEnum);

const OPTION_LABEL_BY_FREQUENCY_TYPE: Partial<
  Record<FrequencyTypeEnum, string>
> = {
  [CollectionPlanFrequencyEnum.WEEK]: "Weekly",
  [CollectionPlanFrequencyEnum.MONTH]: "Monthly",
  [CollectionPlanFrequencyEnum.QUARTER]: "Quarterly",
  [CollectionPlanFrequencyEnum.SEMI_ANNUAL]: "Semi-annually",
  [CollectionPlanFrequencyEnum.ANNUAL]: "Annually",
  [CollectionPlanFrequencyEnum.DOES_NOT_REPEAT]: "Does not repeat",
  [CollectionPlanFrequencyEnum.COMPANY_INITIATED]: "Company-initiated",
};

export const COLLECTION_PLAN_FREQUENCY_TYPE_OPTIONS = toOptions(
  OPTION_LABEL_BY_FREQUENCY_TYPE,
  false
);

const LABEL_BY_TASK_ASSIGNMENT_TYPE: Partial<
  Record<TaskAssignmentTypeEnum, string>
> = {
  [TaskAssignmentTypeEnum.REPORTER]: "Entity",
  [TaskAssignmentTypeEnum.DATA_SERVICES]: "Data services",
  [TaskAssignmentTypeEnum.MEMBERS]: "Specific user(s)",
};

export const FREQUENCY_TYPE_OPTIONS = toOptions(LABEL_BY_FREQUENCY_TYPE, false);

export const PERIOD_TYPE_OPTIONS = toOptions(LABEL_BY_PERIOD_TYPE, false);
export const TASK_ASSIGNMENT_TYPE_OPTIONS = toOptions(
  LABEL_BY_TASK_ASSIGNMENT_TYPE,
  false
);
export const SCALE_OPTIONS = toOptions(LABEL_BY_SCALE, false);

export function getSuffixPlaceholder(
  scale?: ScaleEnum | null,
  returnType?: ReturnTypeEnum | null
): string {
  if (scale === ScaleEnum.MILLIONS) return "e.g. MM";
  if (scale === ScaleEnum.THOUSANDS) return "e.g. K";
  if (returnType === ReturnTypeEnum.PERCENT) return "e.g. BPS";
  return "e.g. units";
}

export const DATABASE_COLUMN_PREFIX = "dcol";
export const RICH_TEXT_QUERY_PREFIX = "rtqy";
export const RICH_TEXT_PREFIX = "rich";
export const DATABASE_ROW_PREFIX = "drow";
export const MAPPING_PREFIX = "map";
export const DATA_EXTRACTION_PREFIX = "dext";
export const LENS_CAPTURE_PREFIX = "lcap";
export const VIEW_COLUMN_PREFIX = "vcol";
export const BLOCK_PREFIX = "bloc";
export const WORKFLOW_STEP_PREFIX = "step";
export const VIEW_PREFIX = "view";
export const DYNAMIC_CHART_PREFIX = "dcht";
export const GRID_CELL_PREFIX = "cell";
export const GRID_PREFIX = "grid";
export const GRID_ROW_PREFIX = "grow";
export const GRID_COL_PREFIX = "gcol";
export const REPORT_INSTANCE_PREFIX = "rpti";
export const WIDGET_PREFIX = "wigt";
export const REPORT_WIDGET_PREFIX = "rwgt";
// Instance-level widget additions are stored as ReportWidgetEdit rows whose row
// id doubles as the rendered widget id — mint with the edit prefix so backend
// id-shape routing (widget_id vs widget_edit_id FK) stays consistent.
export const REPORT_WIDGET_EDIT_PREFIX = "rwed";
export const VIEW_GROUP_PREFIX = "vgrp";
export const FIELD_PREFIX = "fld";
export const VIEW_FILTER_PREFIX = "vfil";
export const VIEW_ATTRIBUTE_PREFIX = "vatr";
export const VIEW_ORDER_PREFIX = "vord";

export const REPORT_PAGE_PREFIX = "rptp";
export const REPORT_INSTANCE_PAGE_PREFIX = "ripp";
export const REPORTER_PREFIXES = ["rptr", "enty"];

export const DEFAULT_FISCAL_YEAR_END_MONTH = 12;

export const VIEW_GROUP_DEFAULT_PROPERTY = "Name";

export const MASK_BY_RETURN_TYPE = {
  [ReturnTypeEnum.CURRENCY]: "currency",
  [ReturnTypeEnum.NUMBER]: "decimal",
  [ReturnTypeEnum.PERCENT]: "percentage",
};

export const CONDITIONAL_IS_PASSING_COLUMN = {
  name: "In compliance?",
  returnType: ReturnTypeEnum.SINGLE_SELECT,
  reference: ReferenceTypeEnum.IN_COMPLIANCE,
  // SINGLE_SELECT cannot be aggregated numerically. End of Period is the only
  // dtype-agnostic aggregation; defaulting here avoids extraction failures
  // when a newly created covenant is later period-transformed. Users can
  // still override via the field edit modal if they need a different method.
  aggregationType: AggregationTypeEnum.END_OF_PERIOD,
};

export const LABEL_BY_MONTH = {
  [MonthEnum._1]: "January",
  [MonthEnum._2]: "February",
  [MonthEnum._3]: "March",
  [MonthEnum._4]: "April",
  [MonthEnum._5]: "May",
  [MonthEnum._6]: "June",
  [MonthEnum._7]: "July",
  [MonthEnum._8]: "August",
  [MonthEnum._9]: "September",
  [MonthEnum._10]: "October",
  [MonthEnum._11]: "November",
  [MonthEnum._12]: "December",
};

export const LABEL_BY_DELIVERABLE_COLLECTION_STATUS = {
  [DeliverableCollectionStatusEnum.ACTIVE]: "Active",
  [DeliverableCollectionStatusEnum.INACTIVE]: "Inactive",
};

export const LABEL_BY_DELIVERABLE_COLLECTION_STRATEGY = {
  [DeliverableCollectionStrategyEnum.OFF_PLATFORM]: "Off platform",
  [DeliverableCollectionStrategyEnum.ON_PLATFORM]: "On platform",
};

export const LABEL_MAP_BY_REFERENCE_FIELD = {
  [ReferenceFieldEnum.ENTITY_DELIVERABLE_COLLECTION_STATUS]:
    LABEL_BY_DELIVERABLE_COLLECTION_STATUS,
  [ReferenceFieldEnum.ENTITY_DELIVERABLE_COLLECTION_STRATEGY]:
    LABEL_BY_DELIVERABLE_COLLECTION_STRATEGY,
  [ReferenceFieldEnum.ENTITY_FISCAL_YEAR_END]: LABEL_BY_MONTH,
};

export const MONTH_OPTIONS = toOptions(LABEL_BY_MONTH, false);

export const RouteNameMapping: Record<RouteNames, string> = {
  // Cluster scope
  [RouteNames.CLUSTER_SETTINGS]: "/clusters/:clusterId/settings",
  [RouteNames.CLUSTER_MEMBER_TASKS]: "/clusters/:clusterId/my-tasks/*",
  [RouteNames.CLUSTER_DASHBOARDS]: "/clusters/:clusterId/dashboards/*",
  [RouteNames.CLUSTER_DELIVERABLES]: "/clusters/:clusterId/compliance-tracker/",
  [RouteNames.CLUSTER_MEMBER_NOTIFICATION_SETTINGS]:
    "/clusters/:clusterId/settings/notifications/*",

  // cluster-level library pages
  [RouteNames.LIBRARY_ENTITY_PERIODIC_FIELDS]:
    "/clusters/:clusterId/periodic-fields/*",
  [RouteNames.LIBRARY_ENTITY_PERIODIC_FIELDS_FIELD]:
    "/clusters/:clusterId/periodic-fields/:fieldId",
  [RouteNames.LIBRARY_ENTITY_STATIC_FIELDS]:
    "/clusters/:clusterId/static-fields/*",
  [RouteNames.LIBRARY_ENTITY_STATIC_FIELDS_FIELD]:
    "/clusters/:clusterId/static-fields/:fieldId",
  [RouteNames.LIBRARY_ENTITY_ENTITIES]:
    "/clusters/:clusterId/entities-config/*",
  [RouteNames.LIBRARY_ENTITY_DATABASES]:
    "/clusters/:clusterId/databases/*",
  [RouteNames.LIBRARY_ENTITY_DATABASES_DATABASE]:
    "/clusters/:clusterId/databases/:databaseId",
  [RouteNames.LIBRARY_ENTITY_COVENANTS]:
    "/clusters/:clusterId/covenants/*",
  [RouteNames.LIBRARY_ENTITY_COVENANTS_COVENANT]:
    "/clusters/:clusterId/covenants/:covenantId",
  [RouteNames.LIBRARY_ENTITY_ROLES]:
    "/clusters/:clusterId/roles/*",
  [RouteNames.LIBRARY_ENTITY_ROLES_ROLE]:
    "/clusters/:clusterId/roles/:roleId",
  [RouteNames.LIBRARY_ENTITY_TASK_TEMPLATES]:
    "/clusters/:clusterId/data-collection/*",
  [RouteNames.LIBRARY_ENTITY_DASHBOARDS]:
    "/clusters/:clusterId/dashboards/*",
  [RouteNames.LIBRARY_ENTITY_DASHBOARDS_DASHBOARD]:
    "/clusters/:clusterId/dashboards/:dashboardId",
  [RouteNames.LIBRARY_ENTITY_DOCUMENT_TAGS]:
    "/clusters/:clusterId/document-tags/*",
  [RouteNames.LIBRARY_ENTITY_WORKFLOWS]:
    "/clusters/:clusterId/workflows/*",
  [RouteNames.LIBRARY_ENTITY_WORKFLOWS_WORKFLOW]:
    "/clusters/:clusterId/workflows/:workflowId",
  [RouteNames.LIBRARY_ENTITY_TASK_MANAGEMENT]:
    "/clusters/:clusterId/task-management/*",
  [RouteNames.LIBRARY_ENTITY_NOTIFICATION_PREFERENCES]:
    "/clusters/:clusterId/notifications/*",
  [RouteNames.LIBRARY_ENTITY_NOTIFICATION_PREFERENCES_WORKFLOW]:
    "/clusters/:clusterId/notifications/:workflowId",
  [RouteNames.LIBRARY_ENTITY_REPORTS]:
    "/clusters/:clusterId/reports/*",

  // entity scope of entity primitives
  [RouteNames.ENTITY_DASHBOARDS]: "/clusters/:clusterId/entities/:entityId/*",
  [RouteNames.ENTITY_DOCUMENTS]:
    "/clusters/:clusterId/entities/:entityId/documents/*",
  [RouteNames.ENTITY_COMMENTARY_FEED]:
    "/clusters/:clusterId/entities/:entityId/commentary-feed/*",
  [RouteNames.ENTITY_DATA_EXTRACTIONS]:
    "/clusters/:clusterId/entities/:entityId/data-extractions/*",
  [RouteNames.ENTITY_DELIVERABLES]:
    "/clusters/:clusterId/entities/:entityId/deliverables/*",
  [RouteNames.ENTITY_ASSET_ACCOUNTS]:
    "/clusters/:clusterId/entities/:entityId/asset-accounts/*",
  [RouteNames.ENTITY_DATABASES]:
    "/clusters/:clusterId/entities/:entityId/databases/*",
  [RouteNames.ENTITY_DATABASES_DATABASE]:
    "/clusters/:clusterId/entities/:entityId/databases/:databaseId",
  [RouteNames.ENTITY_PERIODIC_FIELDS]:
    "/clusters/:clusterId/entities/:entityId/periodic-fields/*",
  [RouteNames.ENTITY_STATIC_FIELDS]:
    "/clusters/:clusterId/entities/:entityId/static-fields/*",
  [RouteNames.ENTITY_SETTINGS]:
    "/clusters/:clusterId/entities/:entityId/settings/*",
  [RouteNames.ENTITY_WORKFLOWS]:
    "/clusters/:clusterId/entities/:entityId/workflows",
  [RouteNames.ENTITY_COVENANTS]:
    "/clusters/:clusterId/entities/:entityId/covenants/*",
  [RouteNames.ENTITY_COVENANTS_COVENANT]:
    "/clusters/:clusterId/entities/:entityId/covenants/:covenantId",
  [RouteNames.ENTITY_MANAGE_ENTITY]:
    "/clusters/:clusterId/entities/:entityId/:namespaceId/*",
};

export const RouteGroupMapping = {
  [RouteNames.CLUSTER_SETTINGS]: "CLUSTER_SCOPE_SETTINGS",
  [RouteNames.CLUSTER_MEMBER_TASKS]: "CLUSTER_SCOPE_TASKS",
  [RouteNames.CLUSTER_DASHBOARDS]: "CLUSTER_SCOPE_DASHBOARD",
  [RouteNames.CLUSTER_DELIVERABLES]: "CLUSTER_SCOPE_DELIVERABLES",
  [RouteNames.LIBRARY_ENTITY_FIELDS]: "LIBRARY_ENTITY_NAMESPACE",
  [RouteNames.LIBRARY_ENTITY_PERIODIC_FIELDS]: "LIBRARY_ENTITY_PERIODIC_FIELDS",
  [RouteNames.LIBRARY_ENTITY_STATIC_FIELDS]: "LIBRARY_ENTITY_STATIC_FIELDS",
  [RouteNames.LIBRARY_ENTITY_ENTITIES]: "LIBRARY_ENTITY_NAMESPACE",
  [RouteNames.LIBRARY_ENTITY_DOCUMENT_TAGS]: "LIBRARY_ENTITY_NAMESPACE",
  [RouteNames.LIBRARY_ENTITY_DATABASES]: "LIBRARY_ENTITY_NAMESPACE",
  [RouteNames.LIBRARY_ENTITY_COVENANTS]: "LIBRARY_ENTITY_NAMESPACE",
  [RouteNames.LIBRARY_ENTITY_ROLES]: "LIBRARY_ENTITY_NAMESPACE",

  [RouteNames.ENTITY_DASHBOARDS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_DOCUMENTS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_DELIVERABLES]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_ASSET_ACCOUNTS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_DATABASES]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_FIELDS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_PERIODIC_FIELDS]: "ENTITY_PERIODIC_FIELDS",
  [RouteNames.ENTITY_STATIC_FIELDS]: "ENTITY_STATIC_FIELDS",
  [RouteNames.ENTITY_SETTINGS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_WORKFLOWS]: "ENTITY_SCOPE",
  [RouteNames.ENTITY_COVENANTS]: "ENTITY_SCOPE",
};

const INTERPOLATION_VARIABLE_BY_LABEL = {
  [InterpolationVariableEnum.TASK_EFFECTIVE_DATE]: "Period end date",
  [InterpolationVariableEnum.TODAY]: "Today",
  [InterpolationVariableEnum.REPORTER_NAME]: "Company name",
};

export const INTERPOLATION_VARIABLE_OPTIONS = toOptions(
  INTERPOLATION_VARIABLE_BY_LABEL
);

const LABEL_BY_DATE_STRATEGY = {
  [DateStrategyEnum.LATEST]: "Latest",
  [DateStrategyEnum.EXACT]: "Exact",
};
export const DATE_STRATEGY_OPTIONS = toOptions(LABEL_BY_DATE_STRATEGY, false);

const LABEL_BY_DELIVERY_CADENCE = {
  [NotificationDeliveryCadenceEnum.DAILY_DIGEST]: "Daily digest",
  [NotificationDeliveryCadenceEnum.REAL_TIME]: "Real time",
};
export const DELIVERY_CADENCE_OPTIONS = toOptions(
  LABEL_BY_DELIVERY_CADENCE,
  false
);

// Days of week options for daily digest delivery preferences
// Values match Python's weekday() convention: Monday=0, Sunday=6
export const DAY_OF_WEEK_OPTIONS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

const NUMERIC_COMPARATORS = [
  { label: "=", value: "==" },
  { label: "≠", value: "!=" },
  { label: ">", value: ">" },
  { label: "<", value: "<" },
  { label: "≥", value: ">=" },
  { label: "≤", value: "<=" },
  { label: "is empty", value: "isEmpty" },
  { label: "is not empty", value: "isNotEmpty" },
];
const TEXT_OPERATORS = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "notContains" },
  { label: "is empty", value: "isEmpty" },
  { label: "is not empty", value: "isNotEmpty" },
];

const SINGLE_SELECT_OPERATORS = [
  { label: "is", value: "==" },
  { label: "is not", value: "!=" },
  { label: "is not empty", value: "isNotEmpty" },
];

const MULTI_SELECT_SELECT_OPERATORS = [
  { label: "contains", value: "contains" },
  { label: "does not contain", value: "notContains" },
];

export const COMPARATORS_BY_RETURN_TYPE = {
  [ReturnTypeEnum.CURRENCY]: NUMERIC_COMPARATORS,
  [ReturnTypeEnum.NUMBER]: NUMERIC_COMPARATORS,
  [ReturnTypeEnum.PERCENT]: NUMERIC_COMPARATORS,
  [ReturnTypeEnum.SHORT_TEXT]: TEXT_OPERATORS,
  [ReturnTypeEnum.LONG_TEXT]: TEXT_OPERATORS,
  [ReturnTypeEnum.CHECKBOX]: [
    { label: "is", value: "==" },
    { label: "is not", value: "!=" },
  ],
  [ReturnTypeEnum.YES_OR_NO]: [
    { label: "is", value: "==" },
    { label: "is not", value: "!=" },
  ],
  [ReturnTypeEnum.SINGLE_SELECT]: SINGLE_SELECT_OPERATORS,
  [ReturnTypeEnum.MULTI_SELECT]: MULTI_SELECT_SELECT_OPERATORS,
  [ReturnTypeEnum.DATE]: [],
  [ReturnTypeEnum.DOCUMENT]: [],
};

const LABEL_BY_TASK_TYPE: Record<TaskTypeEnum, string> = {
  [TaskTypeEnum.DELIVERABLE]: "Deliverable",
  [TaskTypeEnum.CLUSTER_TASK]: "Cluster task",
  [TaskTypeEnum.DATA_EXTRACTION]: "Data extraction",
  [TaskTypeEnum.BANK_CONNECTION]: "Bank connection",
};

export const TASK_TYPE_OPTIONS = toOptions(LABEL_BY_TASK_TYPE, false);
